import { useTranslation } from 'next-i18next'
import { Html } from '@captaincause/ui/Html'
import Hero from 'blocks/Hero'
import { NavAndFooterLayout } from 'layouts/NavAndFooter'
import { getBanner } from 'pages/api/banner'
import { getImpactCommittee } from 'pages/api/impactCommittee'
import { PageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { Button } from 'components/Button'
import { StrapiImage } from 'components/StapiImage'
import type { Locales } from '@captaincause/types'
import type { NextPageWithLayout } from 'app'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { StrapiImpactCommittee } from 'types/strapi/impactCommittee'

interface PageProps {
  impactCommittee: StrapiImpactCommittee | null
}

const LOCALIZATIONS = ['fr', 'en']

export const getStaticProps: GetStaticProps<PageProps> = async context => {
  const i18n = await injectI18n(context.locale, [
    'footer.component',
    'nav.component',
    'comite-impact.page',
    'metadata',
  ])

  const banner = await getBanner({ locale: context.locale as Locales })

  const impactCommittee = await getImpactCommittee()

  return {
    props: {
      banner,
      impactCommittee,
      localizations: LOCALIZATIONS.map(locale => ({ locale })),
      ...i18n,
    },
    revalidate: 600,
  }
}

// ---

type InferredPageProps = InferGetStaticPropsType<typeof getStaticProps>
const Team: NextPageWithLayout<InferredPageProps> = ({
  impactCommittee,
}: PageProps) => {
  const { t } = useTranslation('comite-impact.page')

  return (
    <>
      <PageHead
        head={{
          title: t('metadata.title'),
          description: t('metadata.description'),
          alternates: [
            {
              locale: 'fr',
              href: 'https://dift.com/comite-impact',
            },
            {
              locale: 'en',
              href: 'https://dift.com/en/comite-impact',
            },
          ],
        }}
      />

      <main id="main">
        <Hero
          title={t('hero.title')}
          description={t('hero.description')}
          ficellos="purple-green-orange"
        />
        <div className="relative mx-auto max-w-[1400px] px-4 py-12 lg:px-8 lg:py-20">
          <h2 className="mb-14 text-center text-5xl">{t('committee')}</h2>

          <div className="mx-auto mb-10 grid max-w-[1000px] grid-cols-1 grid-rows-[auto] gap-6 sm:grid-cols-2 md:grid-cols-3 md:gap-10">
            {impactCommittee?.members.map(member => (
              <Button
                key={member.name}
                href={member.linkedin ?? undefined}
                variant="raw"
                className="w-full max-w-[300px] justify-self-center rounded-xl border border-inside p-8 transition-transform duration-300 ease-out hover:-translate-y-1 hover:shadow-lg focus:-translate-y-1 focus:shadow-lg"
                innerClassName="flex flex-col items-center justify-center"
              >
                {member.image ? (
                  <StrapiImage
                    {...member.image}
                    className="relative mx-auto mb-4 w-full max-w-[200px] rounded-full"
                  />
                ) : null}
                <div className="mb-2 text-center text-xl font-semibold">
                  {member.name}
                </div>
                {member.job ? (
                  <Html className="text-dark-gray">
                    {member.job.replace(/\n/gm, '<br />')}
                  </Html>
                ) : null}
              </Button>
            ))}
          </div>
        </div>
      </main>
    </>
  )
}

Team.Layout = NavAndFooterLayout
Team.pagePropsOnLayout = true

export default Team
